import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { editAppealRequest2 } from "services/appealService";
import { AppealRequest } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  appeal: AppealRequest;
  action: "add" | "edit";
  onClose: (refetch: boolean) => void;
};

const HigherClerkForm: FC<Props> = ({ appeal, onClose, action }) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<{
    org_unit_id: string;
  }>({
    org_unit_id: "",
  });

  const handleChange = (e: any) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  };

  useEffect(() => {
    if (action === "edit" && appeal) {
      setFormState({
        org_unit_id: appeal?.org_unit_id || "",
      });
    }
  }, [action, appeal]);

  const validateForm = () => {
    if (!formState?.org_unit_id) return t("organizational_unit_required");

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);

    editAppealRequest2({
      ...formState,
      id: appeal?.id,
    })
      .then(() => {
        showTooltip(t("successful"), "success");
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };
  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">
          {t("organizational_unit")}
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          {t("organizational_unit_submission_form")}
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="flex flex-col mt-4">
            <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
              {t("select_child_org_units")}
            </label>
            <select
              className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              name="org_unit_id"
              value={formState?.org_unit_id}
              onChange={handleChange}
            >
              <option value="">{t("select_child_org_unit")}</option>
              {loginData?.structure?.child_org_units?.tree?.children[0]?.children?.map(
                (org: any, idx: number) => (
                  <option key={org?.uu_id} value={idx}>
                    {org?.unit_name}
                  </option>
                ),
              )}
            </select>
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                      text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                              ${
                                loading ? "opacity-50 cursor-not-allowed" : ""
                              }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  {t("loading")}
                </div>
              ) : action === "edit" ? (
                t("update")
              ) : (
                t("submit")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HigherClerkForm;
